import type { FeatureSet as BackendFeatureSet } from "@mm/metx-workbench.meteomatics.com";

/**
 * JSON-compatible value used by feature set config.
 *
 * @example
 * const value: JsonValue = { tools: { map: true } };
 */
export type JsonValue = string | number | boolean | null | JsonValue[] | JsonObject;

/**
 * JSON-compatible object used by feature set config.
 *
 * @example
 * const config: JsonObject = { tools: { map: true } };
 */
export type JsonObject = {
  [key: string]: JsonValue;
};

/**
 * Frontend-safe feature set shape with a flexible config payload.
 *
 * @example
 * const fs: FeatureSet = {
 *   id: 1,
 *   name: "default",
 *   description: null,
 *   config: { tools: { map: true } },
 * };
 */
export type FeatureSet = {
  id: number;
  name: string;
  description: string | null;
  config: JsonObject | null;
};

/**
 * Checks whether a value is a plain object (not null or an array).
 *
 * @example
 * isRecord({ a: 1 }); // true
 */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Convert unknown backend payload into a safe frontend `FeatureSet`.
 * Returns null if required fields are missing or invalid.
 *
 * @example
 * const fs = toFeatureSet(payload);
 * if (!fs) return;
 */
export const toFeatureSet = (raw: unknown): FeatureSet | null => {
  if (!isRecord(raw)) {
    return null;
  }

  const id = typeof raw.id === "number" && Number.isFinite(raw.id) ? raw.id : null;
  const name = typeof raw.name === "string" ? raw.name : null;

  if (id === null || name === null) {
    return null;
  }

  return {
    id,
    name,
    description: typeof raw.description === "string" ? raw.description : null,
    config: isRecord(raw.config) ? (raw.config as JsonObject) : null,
  };
};

/**
 * Safely read a nested config value by dot-separated path.
 * Returns undefined if the path is missing or invalid.
 *
 * @example
 * const enabled = getFeatureValue(featureSet.config, "tools.map.enabled");
 */
export const getFeatureValue = (config: FeatureSet | null, path: string): JsonValue | undefined => {
  if (!config || !path) {
    return undefined;
  }
  const parts = path.split(".").filter(Boolean);
  if (parts.length === 0) {
    return undefined;
  }

  let current: JsonValue = config;
  if (isRecord(current) && "config" in current) {
    current = current.config as JsonValue;
  }
  for (const key of parts) {
    if (!isRecord(current)) {
      return undefined;
    }
    current = current[key] as JsonValue;
    if (current === undefined) {
      return undefined;
    }
  }

  return current;
};

export type { BackendFeatureSet };
